import * as React from 'react';

/**
 * Base dialog context type shared by all drawer dialogs.
 * Contains common state for drawer appearance, navigation, and footer.
 *
 * @template TFooterOptions - The type of footer options (CreateFooterOptions or SaveFooterOptions)
 */
export type BaseDialogContextType<TFooterOptions> = {
  // Drawer state
  fullSize: boolean;
  setFullSize: (fullSize: boolean) => void;
  dialogTitle: string;
  setDialogTitle: (title: string) => void;
  headerBackButton: (() => void) | null;
  setHeaderBackButton: (onBack: (() => void) | null) => void;

  // Panel navigation/loading
  isLoading: boolean;
  setIsLoading: (isLoading: boolean) => void;

  // Footer state
  footerOptions: TFooterOptions;
  setFooterOptions: (options: TFooterOptions) => void;
};

/**
 * Default empty context value for creating contexts
 */
export const createBaseDialogContextDefaults = <
  TFooterOptions
>(): BaseDialogContextType<TFooterOptions> => ({
  fullSize: false,
  setFullSize: () => {},
  dialogTitle: '',
  setDialogTitle: () => {},
  headerBackButton: null,
  setHeaderBackButton: () => {},
  isLoading: false,
  setIsLoading: () => {},
  footerOptions: {} as TFooterOptions,
  setFooterOptions: () => {},
});

/**
 * Hook to create the base dialog context value with common state management.
 * Use this hook in domain-specific context hooks to avoid duplicating state.
 *
 * @param defaultTitle - The default dialog title
 * @returns Base dialog context value with all common state and setters
 *
 * @example
 * ```tsx
 * export const useProjectResourceDialogContextValue = () => {
 *   const baseContext = useBaseDialogContextValue<CreateFooterOptions>(
 *     copy.components.projectResourceDialog.resourceTypePanelV2.titleAdd
 *   );
 *
 *   // Add domain-specific state
 *   const [selectedFiles, setSelectedFiles] = React.useState<File[]>([]);
 *
 *   return React.useMemo(
 *     () => ({
 *       ...baseContext,
 *       selectedFiles,
 *       setSelectedFiles,
 *     }),
 *     [baseContext, selectedFiles]
 *   );
 * };
 * ```
 */
export const useBaseDialogContextValue = <TFooterOptions>(
  defaultTitle: string
): BaseDialogContextType<TFooterOptions> => {
  const [fullSize, setFullSize] = React.useState(false);
  const [dialogTitle, setDialogTitle] = React.useState(defaultTitle);
  const [headerBackButton, setHeaderBackButton] = React.useState<
    (() => void) | null
  >(null);
  const [isLoading, setIsLoading] = React.useState(false);
  const [footerOptions, setFooterOptions] = React.useState<TFooterOptions>(
    {} as TFooterOptions
  );

  return React.useMemo(
    () => ({
      fullSize,
      setFullSize,
      dialogTitle,
      setDialogTitle,
      headerBackButton,
      setHeaderBackButton,
      isLoading,
      setIsLoading,
      footerOptions,
      setFooterOptions,
    }),
    [fullSize, dialogTitle, headerBackButton, isLoading, footerOptions]
  );
};
